import type { CoaImportRow, CoaValidationLists } from '@taqon/contracts';
import type { Sheet } from './xlsx';

/**
 * The approved COA workbook's columns and the import fields they fill (FRD-FIN-015). The header
 * text is matched exactly as the approved workbook writes it; a column the import does not take
 * (`COA Code`, which repeats `L5 Code`) is ignored. No value is changed here: the API validates
 * and reports every row (FRD-FIN-016).
 */
const COA_COLUMNS: Readonly<Record<string, keyof CoaImportRow>> = {
  'L1 Code': 'l1Code',
  'L1 Name': 'l1Name',
  'L2 Code': 'l2Code',
  'L2 Name': 'l2Name',
  'L3 Code': 'l3Code',
  'L3 Name': 'l3Name',
  'L4 Code': 'l4Code',
  'L4 Name': 'l4Name',
  'L5 Code': 'l5Code',
  'Account Name': 'accountName',
  'Account Type': 'accountType',
  'Financial Statement': 'financialStatement',
  'Normal Balance': 'normalBalance',
  'Posting Account?': 'postingAccount',
  'CAPEX/OPEX': 'capexOpex',
  'Direct/Indirect': 'directIndirect',
  'Control Account?': 'controlAccount',
  'Reconciliation Required?': 'reconciliationRequired',
  'VAT Applicable?': 'vatApplicable',
  'VAT Treatment': 'vatTreatment',
  Module: 'module',
  'Cost Center Required?': 'costCenterRequired',
  'Project Required?': 'projectRequired',
  'Client Required?': 'clientRequired',
  'Supplier Required?': 'supplierRequired',
  'Employee Required?': 'employeeRequired',
  'Asset Required?': 'assetRequired',
  Description: 'description',
  Status: 'status',
  'Effective Date': 'effectiveDate',
  Remarks: 'remarks',
};

/** The `Validation Lists` sheet's columns (FRD-FIN-015: permitted values travel with the load). */
const LIST_COLUMNS: Readonly<Record<string, keyof CoaValidationLists>> = {
  'Account Type': 'accountType',
  'Financial Statement': 'financialStatement',
  'Normal Balance': 'normalBalance',
  'CAPEX/OPEX': 'capexOpex',
  'Direct/Indirect': 'directIndirect',
  Status: 'status',
  'Posting Account?': 'postingAccount',
  'Control Account?': 'controlAccount',
  'Reconciliation Required?': 'reconciliationRequired',
  'VAT Applicable?': 'vatApplicable',
};

export const COA_SHEET = 'COA Master';
export const LISTS_SHEET = 'Validation Lists';

export interface WorkbookPayload {
  readonly rows: CoaImportRow[];
  readonly validationLists?: CoaValidationLists;
}

/** The payload and, for each of its rows, the sheet row it came from (1-based, as Excel shows). */
export interface WorkbookImport {
  readonly payload: WorkbookPayload;
  readonly sheetRows: readonly number[];
}

/**
 * The import payload from the workbook's `COA Master` and `Validation Lists` sheets. Blank rows are
 * skipped; every other row is sent, so a defective row comes back as a rejection with its reason
 * rather than disappearing here.
 */
export function payloadFromWorkbook(sheets: ReadonlyMap<string, Sheet>): WorkbookImport {
  const master = sheets.get(COA_SHEET);
  if (master === undefined || master.length === 0) {
    throw new Error(`The workbook has no "${COA_SHEET}" sheet.`);
  }
  const header = master[0] ?? [];
  const fields = header.map((title) => COA_COLUMNS[title.trim()]);
  if (!fields.includes('l5Code')) {
    throw new Error(`The "${COA_SHEET}" sheet has no "L5 Code" column.`);
  }
  const rows: CoaImportRow[] = [];
  const sheetRows: number[] = [];
  for (const [offset, cells] of master.slice(1).entries()) {
    if (cells.every((cell) => cell.trim() === '')) continue;
    sheetRows.push(offset + 2);
    const row: Record<string, string> = {};
    cells.forEach((cell, index) => {
      const field = fields[index];
      if (field !== undefined && cell.trim() !== '') row[field] = cell.trim().slice(0, 200);
    });
    rows.push(row);
  }
  const lists = sheets.get(LISTS_SHEET);
  if (lists === undefined || lists.length === 0) return { payload: { rows }, sheetRows };
  const listHeader = lists[0] ?? [];
  const validationLists: Record<string, string[]> = {};
  listHeader.forEach((title, column) => {
    const key = LIST_COLUMNS[title.trim()];
    if (key === undefined) return;
    const values = lists
      .slice(1)
      .map((cells) => (cells[column] ?? '').trim())
      .filter((value) => value !== '');
    if (values.length > 0) validationLists[key] = values;
  });
  return {
    payload: { rows, validationLists },
    sheetRows,
  };
}
